/**
 * "Setup": how the test was set up, collapsed by default (replaces the old
 * Settings tab). Goal at launch, versions with preview links, split, audience
 * restrictions, counting, minimum and maximum run, dates and IDs. Read-only;
 * a lock (tooltip only) while the test runs.
 */

import { useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon, SquareLock02Icon } from "@hugeicons/core-free-icons"
import { Card } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import { Hint } from "@/components/experiments/results/ResultRows"

export type SetupRow = {
  label: string
  value: string
  links?: Array<{ label: string; href: string }>
  mono?: boolean
}

export function SetupDisclosure({ rows, isActive, defaultOpen = false }: { rows: SetupRow[]; isActive: boolean; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card className="gap-0 py-0">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-5 py-[14px] text-left">
          <span className="text-[15px] font-semibold leading-5 text-foreground">Setup</span>
          <span className="flex items-center gap-2 text-muted-foreground">
            {isActive && (
              <Hint text="Locked while the test runs">
                <span aria-label="Locked while the test runs">
                  <HugeiconsIcon icon={SquareLock02Icon} size={14} />
                </span>
              </Hint>
            )}
            <HugeiconsIcon icon={ArrowDown01Icon} size={16} className={cn("shrink-0 transition-transform", open && "rotate-180")} />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <dl className="grid border-t border-border/40 px-5 py-2 sm:grid-cols-2 sm:gap-x-8">
            {rows.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 border-b border-border/60 py-2.5 last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0">
                <dt className="shrink-0 text-xs leading-4 text-muted-foreground">{row.label}</dt>
                <dd className={cn("min-w-0 text-right text-[13px] leading-4 text-foreground", row.mono && "font-mono text-xs")}>
                  <span className="break-words">{row.value}</span>
                  {row.links && row.links.length > 0 && (
                    <span className="mt-1 flex flex-wrap justify-end gap-x-3 gap-y-1">
                      {row.links.map((l) => (
                        <a
                          key={l.label}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                        >
                          Preview {l.label}
                        </a>
                      ))}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
